import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Play, Award, X } from "lucide-react";
import api, { embedUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import { FadeUp, Eyebrow, LineReveal } from "@/components/Motion";
import ProjectCard, { CategoryChip } from "@/components/ProjectCard";
import { getVideoLabel, getVideoName } from "@/lib/video";

const getProjectStatus = (project) => {
  if (project.status) return project.status;
  return project.in_development ? "In Development" : "Released";
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [playing, setPlaying] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  useEffect(() => {
    setP(null);
    setNotFound(false);
    setPlaying(false);

    api
      .get(`/projects/${slug}`)
      .then((r) => {
        const project = r.data;
        setP(project);

        if (window.gtag) {
          window.gtag("event", "project_view", {
            project_title: project.title,
            project_slug: project.slug,
            project_category: project.category,
            project_type: project.type || "unknown",
            project_status:
              project.category === "original"
                ? getProjectStatus(project)
                : "service",
          });
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 pt-16" data-testid="project-not-found">
        <SEO title="Project Not Found" noindex />
        <h1 className="font-display text-3xl font-bold">Project not found</h1>
        <Link to="/" className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream dark:bg-cream dark:text-ink">Back to work</Link>
      </div>
    );
  }

  if (!p) {
    return <div className="flex min-h-[70vh] items-center justify-center pt-16 text-muted-foreground" data-testid="project-loading">Loading…</div>;
  }

  const playVideo = () => {
    if (window.gtag) {
      window.gtag("event", "video_play", {
        project_title: p.title,
        project_slug: p.slug,
        project_category: p.category,
        project_type: p.type || "unknown",
        video_type: p.video_type || "Other",
      });
    }

    setPlaying(true);
  };

  const embed = embedUrl(p.trailer_url);
  const isService = p.category === "service";
  const isOriginal = p.category === "original";
  const projectStatus = getProjectStatus(p);

  return (
    <div data-testid={`project-page-${p.slug}`}>
      <SEO
  title={p.title}
  noindex={isOriginal && p.in_development === true}
  description={
    (
      p.tagline ||
      p.synopsis ||
      "Explore this Incolour Films production, including project details, credits and production information."
    ).slice(0, 160)
  }
  image={p.poster}
  path={`/projects/${p.slug}`}
  type="website"
  jsonLd={{
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CreativeWork",
        name: p.title,
        description:
          p.synopsis ||
          p.tagline ||
          "An Incolour Films production.",
        image: p.poster,
        url: `https://incolourfilms.ca/projects/${p.slug}`,
        creator: {
          "@type": "Organization",
          name: "Incolour Films",
          url: "https://incolourfilms.ca",
        },
        ...(p.trailer_url
          ? {
              video: {
                "@type": "VideoObject",
                name: getVideoName(p.title, p.video_type),
                description:
                  p.synopsis ||
                  p.tagline ||
                  `Video for ${p.title}.`,
                thumbnailUrl: p.poster ? [p.poster] : undefined,
                embedUrl: embedUrl(p.trailer_url),
                ...((p.created_at || "").slice(0, 10)
                  ? { uploadDate: p.created_at.slice(0, 10) }
                  : {}),
              },
            }
          : {}),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: isService ? "Service Production" : "Original Work",
            item: isService
              ? "https://incolourfilms.ca/service"
              : "https://incolourfilms.ca/original",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: p.title,
            item: `https://incolourfilms.ca/projects/${p.slug}`,
          },
        ],
      },
    ],
  }}
/>
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[85svh] items-end overflow-hidden bg-ink pt-16 text-cream">
        <motion.img
          src={p.poster}
          alt={p.title}
          loading="eager"
          fetchPriority="high"
          style={{ y: bgY }}
          className="absolute inset-0 h-[115%] w-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <Link to={p.category === "service" ? "/service" : "/original"} data-testid="project-back-link" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-cream/70 transition-colors hover:text-orange">
            <ArrowLeft size={14} /> {p.category === "service" ? "Service Production" : "Original Work"}
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <CategoryChip category={p.category} />

            <span className="rounded-full bg-cream/15 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-cream backdrop-blur">
              {p.type}
              {p.year ? ` · ${p.year}` : ""}
            </span>

            {isOriginal && (
              <span className="rounded-full border border-orange/50 bg-orange/10 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-orange backdrop-blur">
                {projectStatus}
              </span>
            )}

            {isService && p.client && (
              <span className="rounded-full bg-cream/15 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-cream backdrop-blur">
                Client — {p.client}
              </span>
            )}

            {p.imdb_url && (
              <a
                href={p.imdb_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="project-imdb-link"
                className="rounded-full bg-cream/15 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-cream backdrop-blur transition-colors hover:bg-orange"
              >
                IMDb
              </a>
            )}
          </div>
          {p.credits?.find((c) => /starring|cast/i.test(c.role)) && (
            <FadeUp delay={0.15}>
              <p className="mt-4 text-sm text-cream/80" data-testid="project-starring">
                Starring <span className="font-semibold text-cream">{p.credits.find((c) => /starring|cast/i.test(c.role)).name}</span>
              </p>
            </FadeUp>
          )}
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-6xl" data-testid="project-title">
            <LineReveal lines={[p.title]} />
          </h1>
          {p.tagline && (
            <FadeUp delay={0.25}>
              <p className="mt-6 max-w-2xl font-serif text-lg italic leading-relaxed text-cream/85 sm:text-xl">{p.tagline}</p>
            </FadeUp>
          )}
          {p.trailer_url && (
            <FadeUp delay={0.35}>
              <button
                onClick={playVideo}
                data-testid="project-play-video-btn"
                className="mt-8 flex items-center gap-3 rounded-full bg-orange px-6 py-3.5 text-sm font-semibold text-cream transition-transform hover:scale-[1.04]"
              >
                <Play size={14} className="fill-cream" /> {getVideoLabel(p.video_type)}
              </button>
            </FadeUp>
          )}
        </div>
      </section>

      {/* VIDEO EMBED */}
      {playing && embed && (
        <section className="bg-ink px-4 pb-16 sm:px-6 lg:px-8" data-testid="video-embed-section">
          <div className="mx-auto aspect-video max-w-5xl overflow-hidden rounded-2xl border border-cream/15">
            <iframe
              src={`${embed}?autoplay=1`}
              title={getVideoName(p.title, p.video_type)}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>
      )}

      {/* PROJECT OVERVIEW */}
      <section
        className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24"
        data-testid="synopsis-section"
      >
        <div className="lg:col-span-4">
          <Eyebrow>{isService ? "Production Overview" : "About the Film"}</Eyebrow>

          <div className="mt-8 divide-y divide-border border-y border-border">
            {p.type && (
              <div className="flex items-baseline justify-between gap-4 py-4">
                <span className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Format
                </span>
                <span className="text-sm font-semibold">{p.type}</span>
              </div>
            )}

            {p.year && (
              <div className="flex items-baseline justify-between gap-4 py-4">
                <span className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Year
                </span>
                <span className="text-sm font-semibold">{p.year}</span>
              </div>
            )}

            {isOriginal && (
              <div className="flex items-baseline justify-between gap-4 py-4">
                <span className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Status
                </span>
                <span className="text-sm font-semibold text-orange">
                  {projectStatus}
                </span>
              </div>
            )}

            {isService && p.client && (
              <div className="flex items-baseline justify-between gap-4 py-4">
                <span className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Client
                </span>
                <span className="text-right text-sm font-semibold">
                  {p.client}
                </span>
              </div>
            )}
          </div>

          {isOriginal && p.awards?.length > 0 && (
            <div className="mt-8">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                Recognition
              </p>

              <ul className="mt-4 space-y-3">
                {p.awards.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <Award
                      size={15}
                      className="mt-0.5 shrink-0 text-orange"
                    />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="lg:col-span-8">
          <FadeUp>
            <p className="text-lg leading-relaxed sm:text-xl">
              {p.synopsis}
            </p>
          </FadeUp>

          {isOriginal && p.director_note && (
            <FadeUp delay={0.15} className="mt-10">
              <blockquote className="rounded-2xl border border-border bg-lavender/10 p-8">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                  Director&apos;s note
                  {p.director ? ` — ${p.director}` : ""}
                </p>

                <p className="mt-4 font-serif text-xl italic leading-relaxed sm:text-2xl">
                  “{p.director_note}”
                </p>
              </blockquote>
            </FadeUp>
          )}
        </div>
      </section>

      {/* SERVICE CASE STUDY */}
      {isService &&
        (p.service_scope ||
          p.service_locations ||
          p.service_challenge ||
          p.service_outcome) && (
          <section
            className="border-y border-border bg-card py-16 lg:py-24"
            data-testid="service-case-study-section"
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Eyebrow>Case Study</Eyebrow>

              <div className="mt-8 grid gap-6 lg:grid-cols-2">
                {p.service_scope && (
                  <FadeUp>
                    <div className="h-full rounded-2xl border border-border bg-background p-7">
                      <p className="text-xs font-mono uppercase tracking-[0.2em] text-blue">
                        Incolour Scope
                      </p>

                      <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                        {p.service_scope}
                      </p>
                    </div>
                  </FadeUp>
                )}

                {p.service_locations && (
                  <FadeUp delay={0.05}>
                    <div className="h-full rounded-2xl border border-border bg-background p-7">
                      <p className="text-xs font-mono uppercase tracking-[0.2em] text-blue">
                        Locations
                      </p>

                      <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                        {p.service_locations}
                      </p>
                    </div>
                  </FadeUp>
                )}

                {p.service_challenge && (
                  <FadeUp delay={0.1}>
                    <div className="h-full rounded-2xl border border-border bg-background p-7">
                      <p className="text-xs font-mono uppercase tracking-[0.2em] text-blue">
                        Production Requirements
                      </p>

                      <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                        {p.service_challenge}
                      </p>
                    </div>
                  </FadeUp>
                )}

                {p.service_outcome && (
                  <FadeUp delay={0.15}>
                    <div className="h-full rounded-2xl border border-border bg-background p-7">
                      <p className="text-xs font-mono uppercase tracking-[0.2em] text-blue">
                        Outcome
                      </p>

                      <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                        {p.service_outcome}
                      </p>
                    </div>
                  </FadeUp>
                )}
              </div>
            </div>
          </section>
        )}

      {/* STILLS */}
      {p.stills?.length > 0 && (
        <section className="border-y border-border bg-cream2 py-16 dark:bg-card lg:py-24" data-testid="stills-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Eyebrow>{isService ? "Production Stills" : "Stills"}</Eyebrow>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {p.stills.map((s, i) => (
                <FadeUp key={i} delay={i * 0.08}>
                  <button
                    onClick={() => setLightbox(s)}
                    data-testid={`still-${i}`}
                    className="group block w-full overflow-hidden rounded-xl border border-border"
                    aria-label={`Open still ${i + 1}`}
                  >
                    <img src={s} alt={`${p.title} still ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                  </button>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CREDITS */}
      {p.credits?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24" data-testid="credits-section">
          <Eyebrow>{isService ? "Production Credits" : "Credits"}</Eyebrow>
          <div className="mt-8 grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
            {p.credits.map((c, i) => (
              <FadeUp key={i} delay={i * 0.04}>
                <div className="border-t-2 border-ink pt-4 dark:border-cream/30">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-orange">{c.role}</p>
                  <p className="mt-2 font-display text-lg font-bold tracking-tight">{c.name}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      {/* RELATED */}
      {p.related?.length > 0 && (
        <section className="border-t border-border bg-cream2 py-16 dark:bg-card lg:py-24" data-testid="related-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Eyebrow>{isService ? "More Service Work" : "More Original Work"}</Eyebrow>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {p.related.map((r, i) => (
                <ProjectCard key={r.id} p={r} i={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PROJECT CTA */}
      <section className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 py-14 sm:px-6 lg:flex-row lg:items-center lg:px-8">
          <div>
            <Eyebrow>{isService ? "Production in Ontario" : "Develop With Incolour"}</Eyebrow>

            <h2 className="mt-4 max-w-3xl font-display text-2xl font-bold tracking-tight sm:text-3xl">
              {isService
                ? "Planning a production in Toronto or Ontario?"
                : "Have a story you want to move forward?"}
            </h2>
          </div>

          <Link
            to={isService ? "/contact?type=service" : "/contact"}
            className="group inline-flex items-center gap-3 rounded-full bg-orange px-7 py-3.5 text-sm font-semibold text-cream transition-transform hover:scale-[1.04]"
            data-testid="project-contact-link"
          >
            {isService ? "Talk to our production team" : "Bring us your story"}
            <ArrowUpRight
              size={16}
              className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </Link>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/90 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          data-testid="stills-lightbox"
          role="dialog"
          aria-label="Still viewer"
        >
          <button className="absolute right-5 top-5 rounded-full border border-cream/30 p-2 text-cream" aria-label="Close" data-testid="lightbox-close-btn">
            <X size={18} />
          </button>
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            src={lightbox}
            alt="Production still"
            className="max-h-[85vh] max-w-full rounded-lg object-contain"
          />
        </div>
      )}
    </div>
  );
}